/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Voice Studio & Text-to-Speech (TTS) Data Transfer Objects & OpenAPI Schemas
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

/**
 * Supported Text-to-Speech AI Provider Codes.
 */
export const TtsProviderCodeSchema = z
  .enum(['openai', 'elevenlabs', 'google', 'azure'])
  .openapi({
    description: 'Text-to-speech provider identifier',
    example: 'openai',
  });

export type TtsProviderCode = z.infer<typeof TtsProviderCodeSchema>;

/**
 * Supported Output Audio Formats for TTS generation.
 */
export const TtsAudioFormatSchema = z
  .enum(['mp3', 'wav', 'ogg', 'aac', 'opus', 'flac'])
  .openapi({
    description: 'Output audio container format',
    example: 'mp3',
  });

export type TtsAudioFormat = z.infer<typeof TtsAudioFormatSchema>;

/**
 * Voice Gender classifications.
 */
export const VoiceGenderSchema = z
  .enum(['male', 'female', 'neutral'])
  .openapi({
    description: 'Perceived voice gender',
    example: 'female',
  });

export type VoiceGender = z.infer<typeof VoiceGenderSchema>;

/**
 * Advanced Voice Tuning Settings for ElevenLabs and expressive engines.
 */
export const VoiceTuningSettingsSchema = z
  .object({
    stability: z.number().min(0).max(1).optional().openapi({
      description: 'Voice stability factor (0.0: expressive/variable, 1.0: robust/monotone)',
      example: 0.75,
    }),
    similarityBoost: z.number().min(0).max(1).optional().openapi({
      description: 'Clarity and similarity enhancement (0.0: creative, 1.0: strict likeness)',
      example: 0.85,
    }),
    style: z.number().min(0).max(1).optional().openapi({
      description: 'Stylistic exaggeration factor',
      example: 0.2,
    }),
    useSpeakerBoost: z.boolean().optional().openapi({
      description: 'Boost audio volume and speaker presence',
      example: true,
    }),
  })
  .openapi('VoiceTuningSettings');

export type VoiceTuningSettings = z.infer<typeof VoiceTuningSettingsSchema>;

/**
 * Request DTO for Text-to-Speech synthesis.
 */
export const TtsSynthesizeSchema = z
  .object({
    text: z.string().min(1, 'Text cannot be empty').max(5000, 'Maximum text length is 5000 characters').openapi({
      description: 'Raw text or SSML markup to be synthesized into speech',
      example: 'Welcome to Sophia Platform, the next generation edge-native AI SaaS.',
    }),
    provider: TtsProviderCodeSchema.default('openai').openapi({
      description: 'TTS provider engine (openai, elevenlabs, google, azure)',
      example: 'openai',
    }),
    model: z.string().optional().openapi({
      description: 'Specific provider model code (e.g. tts-1, tts-1-hd, eleven_multilingual_v2, neural2)',
      example: 'tts-1',
    }),
    voice: z.string().default('alloy').openapi({
      description: 'Voice identifier or avatar name (e.g. alloy, echo, rachel, en-US-Journey-F)',
      example: 'alloy',
    }),
    language: z.string().optional().openapi({
      description: 'ISO-639-1 language code (e.g. en-US, vi-VN, ja-JP)',
      example: 'en-US',
    }),
    speed: z.number().min(0.25).max(4.0).default(1.0).openapi({
      description: 'Speech playback rate multiplier (0.25x to 4.0x)',
      example: 1.0,
    }),
    pitch: z.number().min(-20.0).max(20.0).default(0.0).openapi({
      description: 'Voice pitch adjustment in semitones / Hz',
      example: 0.0,
    }),
    format: TtsAudioFormatSchema.default('mp3').openapi({
      description: 'Audio output container format',
      example: 'mp3',
    }),
    voiceSettings: VoiceTuningSettingsSchema.optional().openapi({
      description: 'Fine-tuning voice parameters (primarily for ElevenLabs)',
    }),
    expressiveStyle: z.string().optional().openapi({
      description: 'Emotional or situational expressive style (primarily for Azure Speech, e.g. cheerful, empathetic, customerservice)',
      example: 'cheerful',
    }),
    region: z.string().optional().openapi({
      description: 'Target cloud region for regional endpoints (Azure Speech region, e.g. eastus, southeastasia)',
      example: 'eastus',
    }),
    isSsml: z.boolean().default(false).openapi({
      description: 'Whether input text contains SSML tags',
      example: false,
    }),
    returnBinary: z.boolean().default(false).openapi({
      description: 'If true, returns raw binary stream directly instead of saving to R2 and returning JSON',
      example: false,
    }),
  })
  .openapi('TtsSynthesizeRequest');

export type TtsSynthesizeDto = z.infer<typeof TtsSynthesizeSchema>;

/**
 * Response DTO for Text-to-Speech synthesis.
 */
export const TtsSynthesizeResponseSchema = z
  .object({
    success: z.boolean().openapi({ example: true }),
    assetId: z.string().uuid().openapi({
      description: 'Generated media asset unique identifier in R2 and database',
      example: 'c6d2890b-bcf2-4eb5-8e7c-9b8e90fbb662',
    }),
    audioUrl: z.string().url().openapi({
      description: 'Public zero-egress CDN URL to stream or download generated audio',
      example: 'https://media.sophiaplatform.com/audio/ws_01HXYZ123/c6d2890b-bcf2-4eb5-8e7c-9b8e90fbb662.mp3',
    }),
    format: TtsAudioFormatSchema.openapi({ example: 'mp3' }),
    mimeType: z.string().openapi({ example: 'audio/mpeg' }),
    fileSizeBytes: z.number().int().openapi({ example: 45056 }),
    characterCount: z.number().int().openapi({ example: 78 }),
    creditsCost: z.number().openapi({ example: 1 }),
    durationSeconds: z.number().openapi({ example: 4.8 }),
    provider: TtsProviderCodeSchema.openapi({ example: 'openai' }),
    voice: z.string().openapi({ example: 'alloy' }),
    model: z.string().openapi({ example: 'tts-1' }),
    createdAt: z.string().datetime().openapi({ example: '2026-10-20T12:00:00.000Z' }),
  })
  .openapi('TtsSynthesizeResponse');

export type TtsSynthesizeResponseDto = z.infer<typeof TtsSynthesizeResponseSchema>;

/**
 * Catalog item describing an available voice model from any supported provider.
 */
export const VoiceModelInfoSchema = z
  .object({
    id: z.string().openapi({
      description: 'Unique voice identifier (e.g. alloy, rachel, en-US-Journey-F)',
      example: 'alloy',
    }),
    name: z.string().openapi({
      description: 'Human-readable voice display name',
      example: 'Alloy (Neutral & Balanced)',
    }),
    provider: TtsProviderCodeSchema.openapi({ example: 'openai' }),
    gender: VoiceGenderSchema.openapi({ example: 'neutral' }),
    language: z.string().openapi({
      description: 'Primary language locale code',
      example: 'en-US',
    }),
    languageName: z.string().openapi({
      description: 'Display language name in English',
      example: 'English (United States)',
    }),
    description: z.string().optional().openapi({
      description: 'Voice characteristics and recommended use cases',
      example: 'Versatile, clear, and balanced tone for general narration and dialogue.',
    }),
    sampleAudioUrl: z.string().optional().openapi({
      description: 'Public URL or relative path to preview voice audio sample',
      example: 'https://media.sophiaplatform.com/samples/voices/alloy.mp3',
    }),
    sampleAudioKey: z.string().optional().openapi({
      description: 'Relative R2 storage key for voice audio sample (Zero-Hardcoded-Domain)',
      example: 'samples/voices/alloy.mp3',
    }),
    isCustom: z.boolean().default(false).openapi({
      description: 'Whether this is an instant cloned voice specific to the workspace',
      example: false,
    }),
    supportedFormats: z.array(z.string()).openapi({
      description: 'Supported output audio formats for this voice',
      example: ['mp3', 'wav', 'opus', 'aac'],
    }),
    creditsPerChar: z.number().openapi({
      description: 'Cost in credits per single character',
      example: 0.001,
    }),
  })
  .openapi('VoiceModelInfo');

export type VoiceModelInfoDto = z.infer<typeof VoiceModelInfoSchema>;

/**
 * Query schema for listing available voice models with optional filters.
 */
export const ListVoiceModelsQuerySchema = z
  .object({
    provider: TtsProviderCodeSchema.optional().openapi({
      description: 'Filter by AI TTS provider (openai, elevenlabs, google, azure)',
    }),
    language: z.string().optional().openapi({
      description: 'Filter by ISO language code prefix (e.g. en, vi, ja)',
      example: 'en',
    }),
    gender: VoiceGenderSchema.optional().openapi({
      description: 'Filter by voice gender (male, female, neutral)',
    }),
    search: z.string().optional().openapi({
      description: 'Fuzzy search term matching voice name or description',
      example: 'narrator',
    }),
  })
  .openapi('ListVoiceModelsQuery');

export type ListVoiceModelsQueryDto = z.infer<typeof ListVoiceModelsQuerySchema>;

/**
 * Request schema for requesting a presigned audio sample upload URL.
 */
export const VoiceCloneUploadSchema = z
  .object({
    fileName: z.string().min(1).max(255).openapi({
      description: 'Original file name of the audio sample (e.g. speaker_sample.wav, speaker_sample.mp3)',
      example: 'speaker_sample.mp3',
    }),
    fileSizeBytes: z
      .number()
      .int()
      .positive()
      .max(25 * 1024 * 1024, 'Audio sample file size must not exceed 25MB')
      .openapi({
        description: 'Audio file size in bytes (maximum 25MB allowed for voice cloning)',
        example: 3145728,
      }),
    mimeType: z
      .enum([
        'audio/mpeg',
        'audio/mp3',
        'audio/wav',
        'audio/x-wav',
        'audio/ogg',
        'audio/flac',
        'audio/x-m4a',
        'audio/m4a',
        'audio/mp4',
      ])
      .default('audio/mpeg')
      .openapi({
        description: 'MIME type of the audio sample file',
        example: 'audio/mpeg',
      }),
    durationSeconds: z.number().positive().max(1800).optional().openapi({
      description: 'Optional length of audio sample in seconds (recommended 60-300 seconds for optimal cloning fidelity)',
      example: 120,
    }),
  })
  .openapi('VoiceCloneUploadRequest');

export type VoiceCloneUploadDto = z.infer<typeof VoiceCloneUploadSchema>;

/**
 * Response schema providing R2 upload parameters for voice sample audio.
 */
export const VoiceCloneUploadResponseSchema = z
  .object({
    uploadUrl: z.string().url().openapi({
      description: 'Direct or presigned upload URL to upload the raw binary audio file to Cloudflare R2',
      example: 'https://api.sophiaplatform.com/api/v1/workspaces/ws_01HXYZ123/voice/clone/direct-upload?storageKey=voice-samples/ws_01HXYZ123/sample_123.mp3',
    }),
    storageKey: z.string().openapi({
      description: 'Internal R2 storage key where the sample file will reside',
      example: 'voice-samples/ws_01HXYZ123/sample_123.mp3',
    }),
    publicUrl: z.string().url().openapi({
      description: 'Target public zero-egress CDN URL once uploaded',
      example: 'https://media.sophiaplatform.com/voice-samples/ws_01HXYZ123/sample_123.mp3',
    }),
    expiresAt: z.string().datetime().openapi({
      description: 'ISO-8601 expiration timestamp for this upload authorization (1 hour valid)',
      example: '2026-10-20T13:00:00.000Z',
    }),
  })
  .openapi('VoiceCloneUploadResponse');

export type VoiceCloneUploadResponseDto = z.infer<typeof VoiceCloneUploadResponseSchema>;

/**
 * Request schema for creating an AI cloned voice using uploaded sample audio files.
 */
export const VoiceCloneRequestSchema = z
  .object({
    name: z.string().min(2).max(100).openapi({
      description: 'Human-readable unique display name for the cloned voice avatar',
      example: 'Sophia Prime Narrator (Cloned)',
    }),
    sampleStorageKeys: z
      .array(z.string().min(1))
      .min(1, 'At least 1 audio sample storage key is required')
      .max(25, 'Maximum 25 audio samples can be combined for voice cloning')
      .openapi({
        description: 'Array of R2 storage keys corresponding to previously uploaded audio samples',
        example: ['voice-samples/ws_01HXYZ123/sample_123.mp3'],
      }),
    description: z.string().max(500).optional().openapi({
      description: 'Detailed description of the voice tone, accent, and intended use case',
      example: 'Professional studio female voice for product walkthroughs and keynote videos.',
    }),
    gender: VoiceGenderSchema.optional().openapi({
      description: 'Perceived gender profile of the voice avatar',
      example: 'female',
    }),
    language: z.string().default('en-US').openapi({
      description: 'Primary language locale tag spoken in the sample files (e.g. en-US, vi-VN)',
      example: 'en-US',
    }),
    labels: z.record(z.string()).optional().openapi({
      description: 'Custom key-value metadata tags attached to this cloned voice (accent, age, mood)',
      example: { accent: 'American', use_case: 'narrative', age: 'young' },
    }),
  })
  .openapi('VoiceCloneRequest');

export type VoiceCloneRequestDto = z.infer<typeof VoiceCloneRequestSchema>;

/**
 * Response schema when a cloned voice is successfully created and registered.
 */
export const VoiceCloneResponseSchema = z
  .object({
    voiceId: z.string().openapi({
      description: 'Generated ElevenLabs voice ID or unique identifier for the custom voice',
      example: '21m00Tcm4TlvDq8ikWAM',
    }),
    name: z.string().openapi({
      description: 'Voice display name',
      example: 'Sophia Prime Narrator (Cloned)',
    }),
    provider: z.literal('elevenlabs').openapi({
      description: 'Underlying voice cloning engine provider',
      example: 'elevenlabs',
    }),
    assetId: z.string().uuid().openapi({
      description: 'Database media asset UUID tracking this cloned voice profile',
      example: 'f87a8f8e-49b2-4d04-a15d-83b63cece832',
    }),
    sampleAudioUrl: z.string().url().optional().openapi({
      description: 'Public preview URL to listen to the original reference sample audio',
      example: 'https://media.sophiaplatform.com/voice-samples/ws_01HXYZ123/sample_123.mp3',
    }),
    labels: z.record(z.string()).default({}).openapi({
      description: 'Custom metadata labels',
      example: { accent: 'American', use_case: 'narrative' },
    }),
    creditsCost: z.number().openapi({
      description: 'Fixed credits deducted from workspace wallet for the voice cloning process',
      example: 10,
    }),
    status: z.enum(['ready', 'processing', 'failed']).default('ready').openapi({
      description: 'Voice cloning readiness status',
      example: 'ready',
    }),
    createdAt: z.string().datetime().openapi({
      description: 'ISO creation timestamp',
      example: '2026-10-20T12:30:00.000Z',
    }),
  })
  .openapi('VoiceCloneResponse');

export type VoiceCloneResponseDto = z.infer<typeof VoiceCloneResponseSchema>;

/**
 * Detailed representation of a workspace custom cloned voice.
 */
export const CustomVoiceItemSchema = z
  .object({
    id: z.string().openapi({
      description: 'Unique custom voice ID usable in TTS synthesis requests',
      example: 'cloned_voice_21m00Tcm4TlvDq8ikWAM',
    }),
    name: z.string().openapi({
      description: 'Cloned voice avatar name',
      example: 'Sophia Prime Narrator (Cloned)',
    }),
    provider: z.literal('elevenlabs').openapi({ example: 'elevenlabs' }),
    gender: VoiceGenderSchema.openapi({ example: 'female' }),
    language: z.string().openapi({ example: 'en-US' }),
    languageName: z.string().openapi({ example: 'English (United States)' }),
    description: z.string().optional().openapi({ example: 'Professional studio female voice.' }),
    sampleAudioUrl: z.string().url().optional().openapi({ example: 'https://media.sophiaplatform.com/voice-samples/ws_01HXYZ123/sample_123.mp3' }),
    isCustom: z.literal(true).openapi({ example: true }),
    assetId: z.string().uuid().openapi({ example: 'f87a8f8e-49b2-4d04-a15d-83b63cece832' }),
    workspaceId: z.string().openapi({ example: 'ws_01HXYZ123' }),
    labels: z.record(z.string()).default({}).openapi({ example: { accent: 'American' } }),
    createdAt: z.string().datetime().openapi({ example: '2026-10-20T12:30:00.000Z' }),
  })
  .openapi('CustomVoiceItem');

export type CustomVoiceItemDto = z.infer<typeof CustomVoiceItemSchema>;

/**
 * Response schema for successful deletion of a custom cloned voice.
 */
export const VoiceCloneDeleteResponseSchema = z
  .object({
    success: z.literal(true).openapi({ example: true }),
    voiceId: z.string().openapi({
      description: 'Identifier of the deleted cloned voice',
      example: '21m00Tcm4TlvDq8ikWAM',
    }),
    message: z.string().openapi({
      description: 'Confirmation message of voice deletion and resource cleanup',
      example: 'Custom cloned voice deleted successfully from workspace and AI provider.',
    }),
  })
  .openapi('VoiceCloneDeleteResponse');

export type VoiceCloneDeleteResponseDto = z.infer<typeof VoiceCloneDeleteResponseSchema>;

// ============================================================================
// WHISPER SPEECH-TO-TEXT (STT) & SUBTITLES SCHEMAS (Task 7.3.1)
// ============================================================================

/**
 * Request schema for requesting an audio upload URL for Whisper transcription.
 */
export const TranscriptionUploadSchema = z
  .object({
    filename: z.string().min(1, 'Filename is required').max(255).openapi({
      description: 'Audio or video file name to transcribe',
      example: 'interview_podcast.mp3',
    }),
    contentType: z.string().default('audio/mpeg').openapi({
      description: 'MIME content type of the media file',
      example: 'audio/mpeg',
    }),
    fileSizeBytes: z.number().positive().max(500 * 1024 * 1024, 'File size cannot exceed 500MB').openapi({
      description: 'Size of audio or video file in bytes (up to 500MB)',
      example: 45000000,
    }),
  })
  .openapi('TranscriptionUpload');

export type TranscriptionUploadDto = z.infer<typeof TranscriptionUploadSchema>;

/**
 * Response schema providing pre-signed direct upload URL for Whisper audio file.
 */
export const TranscriptionUploadResponseSchema = z
  .object({
    uploadUrl: z.string().url().openapi({
      description: 'Pre-authenticated upload URL targeting Cloudflare R2 bucket',
      example: 'https://media.sophiaplatform.com/transcriptions/ws_01HXYZ123/upl_stt_abc123.mp3?token=...',
    }),
    uploadId: z.string().openapi({
      description: 'Unique upload session tracking ID',
      example: 'upl_stt_abc123',
    }),
    r2Key: z.string().openapi({
      description: 'Target Cloudflare R2 object key where the file must be uploaded',
      example: 'transcriptions/ws_01HXYZ123/upl_stt_abc123.mp3',
    }),
    publicUrl: z.string().url().openapi({
      description: 'Public zero-egress CDN URL to access the audio',
      example: 'https://media.sophiaplatform.com/transcriptions/ws_01HXYZ123/upl_stt_abc123.mp3',
    }),
    expiresInSeconds: z.number().default(3600).openapi({
      description: 'URL expiration timeout in seconds',
      example: 3600,
    }),
  })
  .openapi('TranscriptionUploadResponse');

export type TranscriptionUploadResponseDto = z.infer<typeof TranscriptionUploadResponseSchema>;

/**
 * Word-level timestamp metadata.
 */
export const WordTimestampSchema = z
  .object({
    word: z.string().openapi({ description: 'Transcribed word token', example: 'Sophia' }),
    start: z.number().min(0).openapi({ description: 'Start time in seconds', example: 0.24 }),
    end: z.number().min(0).openapi({ description: 'End time in seconds', example: 0.68 }),
    confidence: z.number().min(0).max(1).optional().openapi({ description: 'Confidence score (0.0 to 1.0)', example: 0.98 }),
  })
  .openapi('WordTimestamp');

export type WordTimestampDto = z.infer<typeof WordTimestampSchema>;

/**
 * Segment-level timestamp metadata for subtitle sentences/chunks.
 */
export const SegmentTimestampSchema = z
  .object({
    id: z.number().openapi({ description: 'Sequential segment index', example: 0 }),
    start: z.number().min(0).openapi({ description: 'Start time in seconds', example: 0.0 }),
    end: z.number().min(0).openapi({ description: 'End time in seconds', example: 3.5 }),
    text: z.string().openapi({ description: 'Transcribed phrase or sentence text', example: 'Welcome to Sophia Platform.' }),
    tokens: z.array(z.number()).optional().openapi({ description: 'Model token identifiers' }),
    temperature: z.number().optional().openapi({ example: 0.0 }),
    avgLogprob: z.number().optional().openapi({ example: -0.25 }),
    compressionRatio: z.number().optional().openapi({ example: 1.15 }),
    noSpeechProb: z.number().optional().openapi({ example: 0.002 }),
  })
  .openapi('SegmentTimestamp');

export type SegmentTimestampDto = z.infer<typeof SegmentTimestampSchema>;

/**
 * Request schema to trigger Whisper Speech-to-Text transcription.
 */
export const ProcessTranscriptionSchema = z
  .object({
    audioUrl: z.string().url().optional().openapi({
      description: 'Public or presigned URL to the audio file in Cloudflare R2 or web',
      example: 'https://media.sophiaplatform.com/transcriptions/ws_01HXYZ123/audio.mp3',
    }),
    r2Key: z.string().optional().openapi({
      description: 'Direct Cloudflare R2 object storage key',
      example: 'transcriptions/ws_01HXYZ123/audio.mp3',
    }),
    provider: z.enum(['workers-ai', 'openai']).default('openai').openapi({
      description: 'STT engine provider (openai or workers-ai)',
      example: 'openai',
    }),
    model: z.string().default('whisper-1').openapi({
      description: 'Model identifier (e.g. whisper-1, @cf/openai/whisper-large-v3-turbo, @cf/openai/whisper)',
      example: 'whisper-1',
    }),
    language: z.string().length(2).optional().openapi({
      description: 'Optional ISO 639-1 language code hint (e.g. vi, en, ja, fr)',
      example: 'vi',
    }),
    prompt: z.string().max(1000).optional().openapi({
      description: 'Optional vocabulary or context hint for improved recognition accuracy',
      example: 'Sophia Platform, Cloudflare Workers, Hono, D1, R2',
    }),
    temperature: z.number().min(0).max(1).default(0).openapi({
      description: 'Sampling temperature for recognition variance',
      example: 0,
    }),
    responseFormat: z.enum(['verbose_json', 'json', 'text', 'srt', 'vtt']).default('verbose_json').openapi({
      description: 'Format of raw recognition output',
      example: 'verbose_json',
    }),
    timestampGranularities: z.array(z.enum(['word', 'segment'])).default(['word', 'segment']).openapi({
      description: 'Granularity levels for timestamps',
      example: ['word', 'segment'],
    }),
  })
  .openapi('ProcessTranscription');

export type ProcessTranscriptionDto = z.infer<typeof ProcessTranscriptionSchema>;

/**
 * Detailed representation of a transcription job and its result.
 */
export const TranscriptionDetailSchema = z
  .object({
    id: z.string().uuid().openapi({
      description: 'Unique transcription job identifier',
      example: '9b1deb4d-3b7d-4bad-9bdd-2b0d7b3dcb6d',
    }),
    workspaceId: z.string().openapi({
      description: 'Owning workspace ID',
      example: 'ws_01HXYZ123',
    }),
    status: z.enum(['queued', 'processing', 'completed', 'failed']).openapi({
      description: 'Job execution status',
      example: 'completed',
    }),
    progressPercent: z.number().min(0).max(100).default(0).openapi({
      description: 'Completion progress percentage',
      example: 100,
    }),
    text: z.string().openapi({
      description: 'Full transcribed transcript text',
      example: 'Welcome to Sophia Platform, the next generation edge-native AI SaaS.',
    }),
    language: z.string().openapi({
      description: 'Detected language code',
      example: 'en',
    }),
    duration: z.number().min(0).openapi({
      description: 'Audio duration in seconds',
      example: 120.4,
    }),
    words: z.array(WordTimestampSchema).optional().openapi({
      description: 'Word-level timestamp list',
    }),
    segments: z.array(SegmentTimestampSchema).optional().openapi({
      description: 'Segment-level timestamp list',
    }),
    creditsCost: z.number().min(0).openapi({
      description: 'Total credits deducted for transcription',
      example: 2,
    }),
    audioUrl: z.string().url().optional().openapi({
      description: 'Public URL to the source audio file',
      example: 'https://media.sophiaplatform.com/transcriptions/ws_01HXYZ123/audio.mp3',
    }),
    r2Key: z.string().optional().openapi({
      description: 'Cloudflare R2 storage key',
      example: 'transcriptions/ws_01HXYZ123/audio.mp3',
    }),
    errorMessage: z.string().nullable().optional().openapi({
      description: 'Error reason if job failed',
      example: null,
    }),
    createdAt: z.string().datetime().openapi({
      description: 'Job creation timestamp',
      example: '2026-10-21T07:00:00.000Z',
    }),
    completedAt: z.string().datetime().nullable().optional().openapi({
      description: 'Job completion timestamp',
      example: '2026-10-21T07:00:15.000Z',
    }),
  })
  .openapi('TranscriptionDetail');

export type TranscriptionDetailDto = z.infer<typeof TranscriptionDetailSchema>;

/**
 * Query schema for exporting subtitle or transcript file.
 */
export const TranscriptionExportQuerySchema = z
  .object({
    format: z.enum(['srt', 'vtt', 'json', 'txt']).default('srt').openapi({
      description: 'Export format: SubRip (srt), WebVTT (vtt), JSON (json), Plain text (txt)',
      example: 'srt',
    }),
    cleanText: z
      .string()
      .optional()
      .transform((val) => val === 'true' || val === '1')
      .openapi({
        description: 'Remove filler words in plain text mode',
        example: 'false',
      }),
    includeTimestamps: z
      .string()
      .optional()
      .transform((val) => val === 'true' || val === '1')
      .openapi({
        description: 'Include timestamp markers [HH:MM:SS] in plain text output',
        example: 'false',
      }),
    noCache: z
      .string()
      .optional()
      .transform((val) => val === 'true' || val === '1')
      .openapi({
        description: 'Bypass Cloudflare R2 cache and regenerate subtitle file',
        example: 'false',
      }),
  })
  .openapi('TranscriptionExportQuery');

export type TranscriptionExportQueryDto = z.infer<typeof TranscriptionExportQuerySchema>;

/**
 * Request schema for translating transcription into other languages (Task 7.3.4).
 */
export const TranslateTranscriptionSchema = z
  .object({
    targetLanguages: z
      .array(
        z.string().min(2).max(10).openapi({
          description: 'ISO-639-1 target language code (e.g. "en", "vi", "ja", "ko", "zh", "fr", "de", "es")',
          example: 'vi',
        }),
      )
      .min(1)
      .max(5)
      .openapi({
        description: 'List of target language codes to translate the transcript into',
        example: ['vi', 'en'],
      }),
    mode: z
      .enum(['target_only', 'bilingual'])
      .default('target_only')
      .openapi({
        description:
          'Subtitle display mode: "target_only" replaces original text with translated text; "bilingual" displays original on top and translation underneath',
        example: 'bilingual',
      }),
    model: z
      .string()
      .optional()
      .openapi({
        description:
          'Translation LLM model (e.g. "gpt-4o-mini", "claude-3-5-haiku", "gemini-1.5-flash", "whisper-1")',
        example: 'gpt-4o-mini',
      }),
    preserveFormatting: z
      .boolean()
      .default(true)
      .openapi({
        description: 'Preserve sentence casing and punctuation formatting',
        example: true,
      }),
    prompt: z
      .string()
      .max(1000)
      .optional()
      .openapi({
        description: 'Optional translation context or glossary instructions',
        example: 'Technical terminology for a software engineering conference',
      }),
  })
  .openapi('TranslateTranscription');

export type TranslateTranscriptionDto = z.infer<typeof TranslateTranscriptionSchema>;

/**
 * Individual translated subtitle segment schema.
 */
export const TranslatedSegmentSchema = z
  .object({
    id: z.number().int().openapi({
      description: 'Sequential segment index',
      example: 1,
    }),
    start: z.number().openapi({
      description: 'Segment start time in seconds',
      example: 0.0,
    }),
    end: z.number().openapi({
      description: 'Segment end time in seconds',
      example: 2.5,
    }),
    originalText: z.string().openapi({
      description: 'Original speech text in source language',
      example: 'Hello everyone, welcome to Sophia Platform.',
    }),
    translatedText: z.string().openapi({
      description: 'Translated text in target language',
      example: 'Xin chào mọi người, chào mừng đến với Sophia Platform.',
    }),
    displayCue: z.string().openapi({
      description: 'Formatted cue text ready for subtitle rendering (respects bilingual/target_only mode)',
      example: 'Hello everyone, welcome to Sophia Platform.\nXin chào mọi người, chào mừng đến với Sophia Platform.',
    }),
  })
  .openapi('TranslatedSegment');

export type TranslatedSegmentDto = z.infer<typeof TranslatedSegmentSchema>;

/**
 * Result for a single translated language item.
 */
export const TranslationResultItemSchema = z
  .object({
    language: z.string().openapi({
      description: 'Target language code',
      example: 'vi',
    }),
    languageName: z.string().openapi({
      description: 'Human-readable language name',
      example: 'Vietnamese',
    }),
    text: z.string().openapi({
      description: 'Full translated text transcript',
      example: 'Xin chào mọi người, chào mừng đến với Sophia Platform.',
    }),
    segments: z.array(TranslatedSegmentSchema).openapi({
      description: 'Translated subtitle segments with preserved timestamps',
    }),
    srtUrl: z.string().url().openapi({
      description: 'Direct zero-egress CDN download URL for SubRip (.srt) subtitle file',
      example: 'https://media.sophiaplatform.com/subtitles/ws_01/job_01_vi_bilingual.srt',
    }),
    vttUrl: z.string().url().openapi({
      description: 'Direct zero-egress CDN download URL for WebVTT (.vtt) subtitle file',
      example: 'https://media.sophiaplatform.com/subtitles/ws_01/job_01_vi_bilingual.vtt',
    }),
    r2KeyPrefix: z.string().openapi({
      description: 'Storage key prefix in Cloudflare R2 bucket',
      example: 'subtitles/ws_01/job_01_vi_bilingual',
    }),
    creditsCharged: z.number().openapi({
      description: 'Credits charged for this language translation',
      example: 1,
    }),
  })
  .openapi('TranslationResultItem');

export type TranslationResultItemDto = z.infer<typeof TranslationResultItemSchema>;

/**
 * Full response schema for subtitle translation request.
 */
export const TranslateTranscriptionResponseSchema = z
  .object({
    transcriptionId: z.string().openapi({
      description: 'Source transcription identifier',
      example: 'stt_01HXYZ123',
    }),
    sourceLanguage: z.string().openapi({
      description: 'Detected source language code',
      example: 'en',
    }),
    mode: z.enum(['target_only', 'bilingual']).openapi({
      description: 'Subtitle display mode used for rendering',
      example: 'bilingual',
    }),
    translations: z.array(TranslationResultItemSchema).openapi({
      description: 'List of translation results per requested target language',
    }),
    totalCreditsDeducted: z.number().openapi({
      description: 'Total workspace credits deducted for all translated languages',
      example: 2,
    }),
    completedAt: z.string().datetime().openapi({
      description: 'Translation completion timestamp',
      example: '2026-10-21T07:15:00.000Z',
    }),
  })
  .openapi('TranslateTranscriptionResponse');

export type TranslateTranscriptionResponseDto = z.infer<typeof TranslateTranscriptionResponseSchema>;



